/* import __COLOCATED_TEMPLATE__ from './topic-trends.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import {
  fetchTrendingTopics,
  type TrendingTopic,
} from './topic-trends/trending-topics-data-resources';
import { tracked } from '@glimmer/tracking';
import type { Topic } from '../../ai-insights/fin-insights/topics-data-resources';

export default class TopicTrends extends Component {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: any;

  @tracked showExploreSidePanel = false;
  @tracked exploreTrendingTopic: TrendingTopic | undefined = undefined;
  @tracked exploreConversationCount = 0;

  @use trendingTopics = AsyncData<TrendingTopic[]>(({ abortSignal }) => {
    // Synthetic data is not available for this endpoint yet
    let useSyntheticData = false;
    return fetchTrendingTopics(this.appService.app.id, useSyntheticData, abortSignal);
  });

  get serializedReport() {
    // We use a long time range to ensure outlier conversations are found
    return {
      id: 'topic-trends',
      date_range: {
        selection: 'past_12_weeks',
      },
    };
  }

  get shouldShowEmptyState() {
    return !this.trendingTopics.isLoading && this.trendingTopics.value?.length === 0;
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  get exploreSelectedTopic(): Topic {
    // Returns topic in the shape that the conversation explore panel expects
    return {
      name: this.exploreTrendingTopic?.topicName ?? '',
      description: null,
      subtopics: {},
    };
  }

  get exploreSelectedSubTopic(): string {
    return this.exploreTrendingTopic?.subtopicName ?? '';
  }

  @action
  onShowConversationExplorer(topic: TrendingTopic) {
    if (!this.canAccessDrillIn) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
      return;
    }

    this.exploreTrendingTopic = topic;
    this.showExploreSidePanel = true;
    this.exploreConversationCount = topic.outlierConversationIds.length;
    this.trackAnalyticsEvent('conversation-explorer-opened', 'conversation-explorer', {
      topic: this.exploreTrendingTopic.topicName,
      subtopic: this.exploreTrendingTopic.subtopicName,
      week_start: this.exploreTrendingTopic.weekStart,
    });
  }

  @action
  closeConversationExplorer() {
    this.showExploreSidePanel = false;
    this.exploreTrendingTopic = undefined;
    this.exploreConversationCount = 0;
    this.trackAnalyticsEvent('conversation-explorer-closed', 'conversation-explorer');
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/topic-trends',
      action,
      object,
      ...extraContext,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends': typeof TopicTrends;
  }
}
